import React from 'react';
import Icon from 'react-icons-kit';
import { apple } from 'react-icons-kit/fa/apple';
import { windows } from 'react-icons-kit/fa/windows';
import { linux } from 'react-icons-kit/fa/linux';
import Container from 'common/components/UI/Container';
import TrialDownloadButton from '../../Custom/TrialDownloadButton';
import HomebrewHint from '../../Custom/HomebrewHint';
import { trackDownload } from 'common/analytics';
import { DOWNLOAD_PLATFORMS, downloadPath } from 'common/data/downloads';
import {
  Wrapper,
  Intro,
  Steps,
  Step,
  OsRow,
  CodeFrame,
  CodeBoxes,
  CodeLabel,
  ScanShot,
  Cta,
} from './getstarted.style';

const CTA_BUTTON = {
  type: 'button',
  fontSize: '16px',
  fontWeight: '700',
  borderRadius: '4px',
  pl: '28px',
  pr: '28px',
  colors: 'secondaryWithBg',
  minHeight: 'auto',
  height: '54px',
};

// Each chip opens the installer for that system, so a visitor reading on a Mac
// can still fetch the Windows or Linux build for another computer.
const OS_CHIPS = [
  { slug: 'mac', label: 'macOS', icon: apple },
  { slug: 'windows', label: 'Windows', icon: windows },
  { slug: 'linux', label: 'Linux', icon: linux },
];

// The last section before the footer: the three steps from the installer to the
// first Savings Scan, merged with the closing download button. #how_it_works
// is the section, #try_free the button block.
const GetStartedSection = () => (
  <Wrapper id="how_it_works">
    <Container>
      <Intro>
        <span>GET STARTED</span>
        <h2>From download to your first Savings Scan</h2>
        <p>
          Three steps, all on your own machine. Your AWS requests go straight
          to AWS, with nothing in between.
        </p>
      </Intro>

      <Steps>
        <Step>
          <div className="visual">
            <OsRow>
              {OS_CHIPS.map(({ slug, label, icon }) => (
                <a
                  key={slug}
                  href={downloadPath(slug)}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Download CloudPouch for ${label}`}
                  onClick={() => trackDownload(slug, DOWNLOAD_PLATFORMS[slug].ext)}
                >
                  <Icon icon={icon} size={16} /> {label}
                </a>
              ))}
            </OsRow>
            <CodeFrame aria-hidden="true">
              <i>$</i> brew install cloudpouch
            </CodeFrame>
          </div>
          <div className="body">
            <span className="num">1</span>
            <h3>Download</h3>
            <p>
              Pick the installer for macOS, Windows or Linux. On a Mac,
              Homebrew does the same job in one command.
            </p>
          </div>
        </Step>

        <Step>
          <div className="visual">
            <CodeLabel aria-hidden="true">Your 6-digit code</CodeLabel>
            <CodeBoxes
              role="img"
              aria-label="Mock-up of six boxes for the 6-digit confirmation code"
            >
              <b>4</b>
              <b>8</b>
              <b>1</b>
              <b className="active">7</b>
              <b />
              <b />
            </CodeBoxes>
          </div>
          <div className="body">
            <span className="num">2</span>
            <h3>Register in the app</h3>
            <p>
              Enter your e-mail address, then the 6-digit code we send you.
              Your license key appears in the app on its own. The 7-day Pro
              trial starts the moment you register, with no credit card.
            </p>
          </div>
        </Step>

        <Step>
          <div className="visual">
            <ScanShot
              src="/demo-preview/step5-results.webp"
              width={960}
              height={600}
              loading="lazy"
              alt="The Savings Scan finished panel: potential monthly savings of $1,806.48 with the savings per AWS service, from demo data."
            />
          </div>
          <div className="body">
            <span className="num">3</span>
            <h3>Run your first Savings Scan</h3>
            <p>
              Select one of your AWS profiles with the required permissions.
              The scan checks the supported services that have costs in the selected month.
            </p>
          </div>
        </Step>
      </Steps>

      <Cta id="try_free">
        <h3>Ready to see where your AWS money goes?</h3>
        <TrialDownloadButton buttonStyle={CTA_BUTTON} location="get_started" />
        <HomebrewHint />
        <p>Pro trial: 7 days, no credit card · Free basic plan, free forever</p>
      </Cta>
    </Container>
  </Wrapper>
);

export default GetStartedSection;
